import { type RECONCILIATION_STEP } from 'features/accounting/debts/types';

export enum TICKET_STATUS_KEYWORD {
  REFUND = 'REFUND',
  VOID = 'VOID',
  ISSUE_SUCCESS = 'ISSUE_SUCCESS',
}

export type TAccountingState = {
  reconciliationStep: RECONCILIATION_STEP;
};

/* -------------------------------------------------------------------------- */
/*                     Accounting service - response envelope                 */
/* -------------------------------------------------------------------------- */

/** `metadata` block of every accounting service response (`message` is HTML-entity encoded). */
export type TAccountingResponseInfo = {
  code: number;
  success: boolean;
  message: string | null;
  isSingleResource: boolean;
  totalRecord: number;
  top: number;
  skip: number;
  returnId: string | null;
};

/** Generic accounting service envelope (`GenericResult<T>` on the backend). */
export type TAccountingGenericResult<T> = {
  metadata: TAccountingResponseInfo | null;
  results: T[] | null;
};

/** DataTables shaped payload returned in `results[0]` of list endpoints. */
export type TAccountingDataTablesResult<T> = {
  draw?: number;
  recordsTotal: number;
  recordsFiltered: number;
  data: T[] | null;
  totalRevenue?: number | null;
  startingBalance?: number | null;
  endingBalance?: number | null;
};

/** Raw list response of the accounting service. */
export type TAccountingListResponse<T> = TAccountingGenericResult<
  TAccountingDataTablesResult<T>
>;

/** Raw command (create / update / delete / lock ...) response of the accounting service. */
export type TAccountingCommandResponse = TAccountingGenericResult<unknown>;

/** Unwrapped list result consumed by the UI (same naming as `ListQueryResponse`). */
export type TAccountingListResult<T> = {
  data: T[];
  total_count: number;
};

/** 1-based paging params expected by the accounting service. */
export type TAccountingPagingParams = {
  pageIndex: number;
  pageSize: number;
};

/* -------------------------------------------------------------------------- */
/*                              Deposit transactions                          */
/* -------------------------------------------------------------------------- */

export enum DEPOSIT_TRANSACTION_TYPE {
  CUSTOMER = 1,
  PROVIDER = 2,
}

/** `IsLock` of a deposit transaction; `PENDING` is the pending data listed by "Dữ liệu treo". */
export enum DEPOSIT_TRANSACTION_LOCK {
  UNLOCKED = 0,
  LOCKED = 1,
  PENDING = 2,
}

/** `DepositType` of a deposit transaction (old app "Hình thức": 1 = bank transfer, 2 = cash). */
export enum DEPOSIT_METHOD {
  BANK_TRANSFER = 1,
  CASH = 2,
}

export type TDepositTransaction = {
  id: number;
  depositType: number | null;
  productCode: string | null;
  numberOfDocument: string | null;
  fundsTransfer: string | null;
  partnerCode: string | null;
  transactionDate: string;
  amount: number;
  currentAmount: number | null;
  newAmount: number | null;
  description: string | null;
  agentUserId: number | null;
  bookingType: string | null;
  transactionType: string | null;
  memberId: number | null;
  memberInfo: string | null;
  groupId: number | null;
  groupInfo: string | null;
  userId: number | null;
  isLock: DEPOSIT_TRANSACTION_LOCK | null;
  bankAccountId: number | null;
  submitter: string | null;
  type: DEPOSIT_TRANSACTION_TYPE | null;
  providerCode: string | null;
};

/** Body of `POST /accounting/DepositTransaction` (search). Dates are `YYYY-MM-DD`. */
export type TDepositTransactionListParams = Partial<TAccountingPagingParams> & {
  keyword?: string;
  from?: string;
  to?: string;
  partnerCode?: string;
  providerCode?: string;
  fundsTransfer?: string;
  agentUserId?: number;
  bankAccountId?: number;
  memberId?: number;
  groupId?: number;
  isLock?: DEPOSIT_TRANSACTION_LOCK;
  type?: DEPOSIT_TRANSACTION_TYPE;
};

export type TDepositTransactionListResult =
  TAccountingListResult<TDepositTransaction> & {
    totalRevenue: number;
  };

/** Body of `POST /accounting/CreateDepositTransaction` (`transactionDate` is `YYYY-MM-DDTHH:mm:ss`). */
export type TCreateDepositTransactionPayload = {
  type: DEPOSIT_TRANSACTION_TYPE;
  partnerCode?: string | null;
  providerCode?: string | null;
  transactionDate: string;
  amount: number;
  description?: string | null;
  fundsTransfer?: string | null;
  bankAccountId?: number | null;
  depositType?: DEPOSIT_METHOD | null;
  /** Staff `systemId` picked as "Người thực hiện" (the creator is taken from the token as `agentUserId`). */
  userId?: number | null;
  /** Payer / beneficiary name ("Người nộp"). */
  submitter?: string | null;
  productCode?: string | null;
};

/** Body of `PUT /accounting/UpdateDepositTransaction` (patch semantics: only non-empty fields are applied). */
export type TUpdateDepositTransactionPayload = Partial<
  Omit<TCreateDepositTransactionPayload, 'type'>
> & {
  id: number;
  productCode?: string | null;
};

/* -------------------------------------------------------------------------- */
/*                                  Bank accounts                             */
/* -------------------------------------------------------------------------- */

export type TBankAccount = {
  id: number;
  partnerCode: string | null;
  bankCode: string | null;
  bankName: string | null;
  accountNumber: string | null;
  note: string | null;
};

export type TBankAccountListParams = Partial<TAccountingPagingParams> & {
  keyword?: string;
  bankCode?: string;
};

/* -------------------------------------------------------------------------- */
/*                   Filter lookups (partners, users, 787 data)               */
/* -------------------------------------------------------------------------- */

/** Item of `GET /sales/GetAllPartner` (customer partners, in `results[0].data`). */
export type TAccountingPartner = {
  id: number;
  partnerCode: string | null;
  partnerName: string | null;
  partnerLevel: number | null;
  isActive: boolean;
  /** Accounting code ("Mã kế toán"); the customer payable summary may group a partner under it. */
  accountingCode?: string | null;
};

/** Item of `GET /sales/ListProvider` (providers, in `results[0].data`). */
export type TAccountingProvider = {
  id: number;
  providerCode: string | null;
  providerName: string | null;
  isActive: boolean | null;
  /** Code stored on the tickets (e.g. `AirlineCode` of a shift closing ticket); falls back to `providerCode`. */
  systemCode?: string | null;
  /** Comma separated service codes the provider sells (`AIR`, `KS`, `VC`, `ISR`). */
  type?: string | null;
};

/** Query of `GET /sales/ListProvider`. */
export type TAccountingProviderListParams = Partial<TAccountingPagingParams> & {
  keyword?: string;
};

/** Item of `GET /user/get-all-user` (users of the caller's partner, in `results`). */
export type TAccountingUser = {
  id: number;
  systemId: number | null;
  displayName: string | null;
  email: string | null;
  partnerCode: string | null;
  active: boolean;
};

/** Raw response of `GET /user/get-all-user`. */
export type TAccountingUserListResponse =
  TAccountingGenericResult<TAccountingUser>;

/** Group of the 787 members / groups webhook. */
export type TGroup787 = {
  Id: number;
  Title: string | null;
};

/** Member of the 787 members / groups webhook. */
export type TMember787 = {
  Id: number;
  DisplayName: string | null;
  Phone: string | null;
  Email: string | null;
  GroupId: number | null;
};

/** Payload of the 787 members / groups webhook (returned either as an object or a single-item array). */
export type TMembersGroups787 = {
  groups: TGroup787[];
  members: TMember787[];
};

/** Select option of the 787 member filter (`groupId` narrows the list to the selected group). */
export type TMember787Option = {
  value: string;
  label: string;
  groupId: string | null;
};

/** Generic string-valued Select option used by the accounting filter lookups. */
export type TAccountingSelectOption = {
  value: string;
  label: string;
};

/* -------------------------------------------------------------------------- */
/*                         Liabilities (công nợ) reports                      */
/* -------------------------------------------------------------------------- */

export enum PAYABLE_REPORT_ROW_TYPE {
  DEPOSIT = 1,
  AIRLINE_TICKET = 2,
  // eslint-disable-next-line no-magic-numbers
  HOTEL = 3,
  // eslint-disable-next-line no-magic-numbers
  ENTERTAINMENT_TICKET = 4,
  // eslint-disable-next-line no-magic-numbers
  INSURANCE = 5,
}

/** `TypeFilter` of `ListPayableReport`. */
export enum PAYABLE_REPORT_TYPE_FILTER {
  TOPUP = 1,
  REFUND = 2,
  // eslint-disable-next-line no-magic-numbers
  SALE = 3,
  // eslint-disable-next-line no-magic-numbers
  OTHER = 4,
}

/** Row of `ListPayableReport` / `ListProviderPayableReport` (`amount` is signed). */
export type TPayableReportRow = {
  date: string;
  partnerCode: string | null;
  providerCode: string | null;
  amount: number;
  currentBalance: number;
  endingBalance: number;
  bookingCode: string | null;
  ticketNumber: string | null;
  itinerary: string | null;
  customerName: string | null;
  type: PAYABLE_REPORT_ROW_TYPE;
};

/** Query of `GET /accounting/ListPayableReport` (customer liabilities). Dates are `YYYY-MM-DD`. */
export type TPayableReportParams = Partial<TAccountingPagingParams> & {
  keyword?: string;
  from?: string;
  to?: string;
  bookingCode?: string;
  partnerCode?: string;
  typeFilter?: PAYABLE_REPORT_TYPE_FILTER;
};

/** Query of `GET /accounting/ListProviderPayableReport` (provider liabilities). Dates are `YYYY-MM-DD`. */
export type TProviderPayableReportParams = Omit<
  TPayableReportParams,
  'partnerCode' | 'typeFilter'
> & {
  providerCode?: string;
};

export type TPayableReportResult = TAccountingListResult<TPayableReportRow> & {
  startingBalance: number;
  endingBalance: number;
};

/**
 * Row of `ListCustomerPayableSummary` / `ListProviderPayableSummary`: the period figures of one customer / provider
 * code (the rows have no id, `code` is unique).
 */
export type TPayableSummaryRow = {
  code: string;
  openingBalance: number;
  totalSale: number;
  totalTransaction: number;
  debt: number;
};

/** Filters shared by both payable summaries. Dates are `YYYY-MM-DD`; the service defaults to today. */
type TPayableSummaryBaseParams = Partial<TAccountingPagingParams> & {
  /** Matched (contains, case insensitive) against the row code. */
  keyword?: string;
  from?: string;
  to?: string;
};

/** Query of `GET /accounting/ListCustomerPayableSummary`. */
export type TCustomerPayableSummaryParams = TPayableSummaryBaseParams & {
  customerCode?: string;
};

/** Query of `GET /accounting/ListProviderPayableSummary`. */
export type TProviderPayableSummaryParams = TPayableSummaryBaseParams & {
  providerCode?: string;
};

/** Period totals of the whole filtered summary (not only the current page). */
export type TPayableSummaryTotals = {
  totalSale: number;
  totalTransaction: number;
  totalDebt: number;
};

/**
 * Raw response of both payable summaries: `results[0]` is a DataTables payload plus the period totals
 * (`recordsTotal` counts the rows before the keyword / code filters, `recordsFiltered` after them).
 */
export type TPayableSummaryResponse = TAccountingGenericResult<
  TAccountingDataTablesResult<TPayableSummaryRow> &
    Partial<Record<keyof TPayableSummaryTotals, number | null>>
>;

/** Unwrapped payable summary: one page of rows plus the totals of the whole filtered period. */
export type TPayableSummaryResult = TAccountingListResult<TPayableSummaryRow> &
  TPayableSummaryTotals;

/** Loads one page (1-based) of an accounting list; `null` when the request failed. */
export type TAccountingListPageLoader<T> = (
  pageIndex: number
) => Promise<TAccountingListResult<T> | null>;

/** Loads one page (1-based) of a payable summary; `null` when the request failed. */
export type TPayableSummaryPageLoader = (
  pageIndex: number
) => Promise<TPayableSummaryResult | null>;

/** Payload of `downloadAccountingWorkbook`: columns are the header labels, rows are keyed by column. */
export type TAccountingWorkbookPayload<TColumn extends string> = {
  sheetName: string;
  /** File name before the timestamp, e.g. `TongHopCongNoKhachHang` -> `TongHopCongNoKhachHang_20261101093000.xlsx`. */
  filePrefix: string;
  columns: TColumn[];
  /** Columns formatted as amounts (thousands separator). */
  amountColumns: TColumn[];
  rows: Record<TColumn, string | number>[];
};

/* -------------------------------------------------------------------------- */
/*                         Opening balances (số dư đầu kỳ)                    */
/* -------------------------------------------------------------------------- */

/** `Type` of an opening balance (old app `data-partner-type`): customer partner or provider. */
export enum OPENING_BALANCE_TYPE {
  CUSTOMER = 1,
  PROVIDER = 2,
}

/** `Status` of an opening balance (`null` counts as unconfirmed). A confirmed one can't be updated until unlocked. */
export enum OPENING_BALANCE_CONFIRM_STATUS {
  UNCONFIRMED = 0,
  CONFIRMED = 1,
}

/** Row of `GET /accounting/ListOpeningBalances` (in `results[0].data`, newest `date` first). */
export type TOpeningBalance = {
  id: number;
  /** Customer partner code (customer rows). */
  partnerCode: string | null;
  /** Provider code (provider rows). */
  providerCode: string | null;
  /** Start date ("Thời hạn bắt đầu"), ISO without offset. */
  date: string | null;
  /** Signed opening balance ("Số dư đầu kỳ"). */
  debitBalance: number;
  note: string | null;
  status: OPENING_BALANCE_CONFIRM_STATUS | null;
  /** Reason of the last unlock. */
  remark: string | null;
  type: OPENING_BALANCE_TYPE | null;
};

/**
 * Query of `GET /accounting/ListOpeningBalances`. The service also declares `status` and `date` but ignores them,
 * so they are not sent (those filters are applied in the browser).
 */
export type TOpeningBalanceListParams = Partial<TAccountingPagingParams> & {
  /** Matched (SQL `LIKE`, contains) against the partner code and the note. */
  keyword?: string;
  partnerCode?: string;
  providerCode?: string;
  type?: OPENING_BALANCE_TYPE;
};

/** Filters of `TOpeningBalanceListParams` without the paging (used to load every matching row). */
export type TOpeningBalanceFilterParams = Omit<
  TOpeningBalanceListParams,
  keyof TAccountingPagingParams
>;

/**
 * Body of `POST /accounting/CreateOpeningBalances`. `date` is `DD-MM-YYYY` (parsed with `dd-MM-yyyy`).
 * The service refuses a second opening balance for the same `partnerCode`.
 */
export type TCreateOpeningBalancePayload = {
  type: OPENING_BALANCE_TYPE;
  partnerCode?: string;
  providerCode?: string;
  date: string;
  debitBalance: number;
  note?: string;
};

/**
 * Body of `PUT /accounting/UpdateOpeningBalances`. The partner code can't change; `type`, `providerCode` and
 * `note` are overwritten with the sent values (an omitted one becomes empty), so they must always be sent.
 */
export type TUpdateOpeningBalancePayload = {
  id: number;
  type: OPENING_BALANCE_TYPE;
  providerCode?: string;
  date: string;
  debitBalance: number;
  note?: string;
};

/* -------------------------------------------------------------------------- */
/*            Sales report (customer care - shift closing tickets)            */
/* -------------------------------------------------------------------------- */

/** `IsLock` of a shift closing ticket ("Trạng thái dữ liệu"). */
export enum SHIFT_CLOSING_LOCK_STATUS {
  UNCONFIRMED = 1,
  CONFIRMED = 2,
  /** Pending ticket ("Dữ liệu treo"). */
  // eslint-disable-next-line no-magic-numbers
  PENDING = 3,
}

/** `Status` of a shift closing ticket, as chosen in the create / update form ("Tình trạng", same codes as the old app). */
export enum SHIFT_CLOSING_TICKET_STATUS {
  ISSUE_SUCCESS = 'ISSUE_SUCCESS',
  REFUND = 'REFUND',
  EXCHANGE = 'EXCHANGE',
  EXTRA = 'EXTRA',
  VOID = 'VOID',
}

/** Airline ticket of `GET /cuscare/ListShiftClosingInvoice` (in `results[0].data`). */
export type TShiftClosingTicket = {
  id: number;
  issueDate: string | null;
  ticketNumber: string | null;
  passengerName: string | null;
  bookingCode: string | null;
  agentCode: string | null;
  status: string | null;
  /** Net price ("Giá NET"). */
  totalAmount: number | null;
  /** Provider system code ("Nhà cung cấp"). */
  airlineCode: string | null;
  /** Selling price ("Giá thu"). */
  collection: number | null;
  /** Other cost ("Chi phí khác"). */
  marketingFee: number | null;
  tripCode: string | null;
  /** `systemId` of the staff who issued the ticket ("Người thực hiện"). */
  userId: number | null;
  isLock: SHIFT_CLOSING_LOCK_STATUS | null;
  /** Booking source (`ADC` = HST, `API`, `AIR`). */
  via: string | null;
};

/** Query of `GET /cuscare/ListShiftClosingInvoice`. Dates are `YYYY-MM-DD`. */
export type TSalesReportParams = Partial<TAccountingPagingParams> & {
  keyword?: string;
  from?: string;
  to?: string;
  agentCode?: string;
  bookingCode?: string;
  status?: string;
  airlineCode?: string;
  userId?: number;
  /** Lock state of the listed tickets ("Trạng thái dữ liệu"); the sales report only lists `CONFIRMED`. */
  isLock?: SHIFT_CLOSING_LOCK_STATUS;
  /** Lets the service return large pages for the Excel export. */
  isExport?: boolean;
};

/** Raw list response of `ListShiftClosingInvoice` (`results[0]` also carries the period totals). */
export type TSalesReportResponse = TAccountingGenericResult<
  TAccountingDataTablesResult<TShiftClosingTicket> & {
    netRevenue?: number | null;
    profit?: number | null;
  }
>;

/** Unwrapped sales report: one page of tickets plus the totals of the whole filtered period. */
export type TSalesReportResult = TAccountingListResult<TShiftClosingTicket> & {
  totalRevenue: number;
  netRevenue: number;
  profit: number;
};

/** Flight segment of a ticket (`routes` of `GET /cuscare/GetShiftClosingInvoice`). Dates have no offset. */
export type TShiftClosingRoute = {
  id: number;
  numericalOrder: number | null;
  startPoint: string | null;
  endPoint: string | null;
  /** Departure date-time. */
  startDate: string | null;
  /** Arrival date-time. */
  endDate: string | null;
  class: string | null;
  flightNumber: string | null;
};

/**
 * Ticket of `GET /cuscare/GetShiftClosingInvoice` / `GET /cuscare/GetShiftClosing`: the list fields plus the flight
 * segments, the note and the price breakdown / group file used by the edit form.
 */
export type TShiftClosingTicketDetail = TShiftClosingTicket & {
  routes: TShiftClosingRoute[] | null;
  note: string | null;
  /** `GROUP` (vé đoàn), `SPECIAL` (đặc thù) or empty (vé thông thường). */
  ticketType?: string | null;
  /** Fare ("Giá fare"). */
  fareAmount?: number | null;
  /** Fee ("Phí(Fee)"). */
  feeAmount?: number | null;
  /** Tax ("Thuế(Tax)"). */
  taxAmount?: number | null;
  /** Path of the uploaded group file on the customer care service (e.g. `/uploads/...xlsx`). */
  groupFile?: string | null;
};

/** Raw response of `GET /cuscare/GetShiftClosingInvoice` (the ticket is `results[0].data[0]`). */
export type TShiftClosingTicketDetailResponse = TAccountingGenericResult<
  TAccountingDataTablesResult<TShiftClosingTicketDetail>
>;

/** Body of `PUT /cuscare/UnLockShiftClosingInvoice`, `PUT /cuscare/LockShiftClosing` and `PUT /cuscare/UnLockShiftClosing`. */
export type TShiftClosingLockPayload = {
  ticketIds: number[];
};

/**
 * Input of `POST /cuscare/CreateMultiShiftClosing` ("Thêm báo cáo"): the body is the list of provider report ids,
 * `isPending` is sent as a query param.
 */
export type TCreateMultiShiftClosingPayload = {
  /** Ids of the imported provider report rows to turn into tickets. */
  providerReportIds: number[];
  /** Creates the tickets in the pending data ("Dữ liệu treo") instead of the processing step. */
  isPending?: boolean;
};

/* -------------------------------------------------------------------------- */
/*          Pending data ("Dữ liệu treo") - customer care shift closing        */
/* -------------------------------------------------------------------------- */

/** Query of `GET /cuscare/ListShiftClosing`. Dates are `YYYY-MM-DD`. */
export type TShiftClosingListParams = TSalesReportParams;

/** Raw list response of `GET /cuscare/ListShiftClosing` (DataTables shaped `results[0]`). */
export type TShiftClosingListResponse =
  TAccountingListResponse<TShiftClosingTicket>;

/** Passenger of the create / update multipart body (`Passengers[i].*`). Amounts are VND. */
export type TShiftClosingPassengerPayload = {
  passengerName?: string | null;
  passengerType?: string | null;
  ticketNumber?: string | null;
  /** Selling price ("Giá thu"). */
  collection: number;
  /** Net price ("Giá Net"). */
  totalAmount: number;
  fareAmount: number;
  feeAmount: number;
  taxAmount: number;
  /** Other cost ("Marketing fee" / "Chi phí khác"). */
  marketingFee?: number | null;
  note?: string | null;
};

/** Flight segment of the create / update multipart body (`Routes[i].*`). Dates are `YYYY-MM-DDTHH:mm:ss`. */
export type TShiftClosingRoutePayload = {
  /** Id of an existing segment (update only; `0` adds a segment). */
  id?: number;
  /** 1-based order of the segment (create only). */
  numericalOrder?: number;
  startPoint?: string | null;
  endPoint?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  class?: string | null;
  flightNumber?: string | null;
};

/** Body of `POST /cuscare/CreateShiftClosing`, sent as `multipart/form-data` (see `buildAccountingFormData`). */
export type TCreateShiftClosingPayload = {
  bookingCode: string;
  /** Provider system code ("Nhà cung cấp"). */
  airlineCode: string;
  /** Partner code ("Đối tác"). */
  agentCode: string;
  status: SHIFT_CLOSING_TICKET_STATUS;
  ticketType?: string | null;
  /** `systemId` of the staff who issued the ticket ("Người thực hiện"). */
  userId?: number | null;
  /** Issue date-time (`YYYY-MM-DDTHH:mm:ss`). */
  issueDate?: string | null;
  passengers: TShiftClosingPassengerPayload[];
  routes?: TShiftClosingRoutePayload[];
  groupFile?: File | null;
};

/** Body of `PUT /cuscare/UpdateShiftClosing`, sent as `multipart/form-data`. The issuing staff can't change. */
export type TUpdateShiftClosingPayload = Omit<
  TCreateShiftClosingPayload,
  'userId'
> & {
  id: number;
  /** Itinerary code built from the segments (e.g. `HANSGN`, `HANSGNHAN`), as the old app sent it. */
  tripCode?: string | null;
};

/** Message of the `UpdateTblTicket` webhook ("Làm mới dữ liệu"); `isError` when its source key contains `error`. */
export type TShiftClosingSyncMessage = {
  message: string;
  isError: boolean;
};

/** `queryFn` result of an n8n "Làm mới dữ liệu" webhook (see `requestShiftClosingSyncWebhook`). */
export type TShiftClosingSyncWebhookResult =
  | { data: TShiftClosingSyncMessage[] }
  | { error: { status: number | string; data: unknown } };

/** Body of the `RetriveBooking` webhook ("Lấy dữ liệu tự động"): the PNR is sent as `ticketNumber`. */
export type TRetrieveBookingPayload = {
  ticketNumber: string;
  airlineCode: string;
};

/** Passenger of a retrieved booking. */
export type TRetrievedBookingPassenger = {
  fullName?: string | null;
  /** `ADT` / `CHD` / `INF` (or `ADULT` / `CHILD` / `INFANT`). */
  type?: string | null;
  remark?: string | null;
  passengerKey?: string | null;
};

/** Ticket of a retrieved booking (only passenger tickets have an `ADT` / `CHD` / `INF` type). */
export type TRetrievedBookingTicket = {
  type?: string | null;
  ticketNumber?: string | null;
};

/** Airport of a retrieved flight segment. */
export type TRetrievedBookingAirport = {
  airportCode?: string | null;
};

/** Flight segment of a retrieved booking (dates are ISO strings). */
export type TRetrievedBookingSegment = {
  startDate?: string | null;
  endDate?: string | null;
  startAirport?: TRetrievedBookingAirport | null;
  endAirport?: TRetrievedBookingAirport | null;
  class?: string | null;
  flightNumber?: string | null;
};

/** Outbound / return flight of a retrieved booking. */
export type TRetrievedBookingFlight = {
  segments?: TRetrievedBookingSegment[] | null;
};

/** Booking returned by the `RetriveBooking` webhook (`[0].data`). */
export type TRetrievedBooking = {
  passengers?: TRetrievedBookingPassenger[] | null;
  tickets?: TRetrievedBookingTicket[] | null;
  /** Net price of the whole booking. */
  totalNet?: number | null;
  departureFlight?: TRetrievedBookingFlight | null;
  returnFlight?: TRetrievedBookingFlight | null;
};

/** Raw response of the `RetriveBooking` webhook. */
export type TRetrieveBookingResponse = { data?: TRetrievedBooking | null }[];

/* -------------------------------------------------------------------------- */
/*                     Activity logs ("Lịch sử thao tác")                     */
/* -------------------------------------------------------------------------- */

/** Query of `GET /activity-log/get-by-entity`: the record whose logs are loaded. */
export type TActivityLogEntity = {
  serviceName: string;
  entityName: string;
  entityId: number;
};

/** Item of `GET /activity-log/get-by-entity` (in `results`). `createdAt` has no offset. */
export type TActivityLog = {
  id: number;
  userId: number | null;
  displayName: string | null;
  serviceName: string | null;
  /** `CREATE`, `UPDATE`, `DELETE`, `APPROVE` (confirm) or `REJECT` (unlock). */
  actionType: string | null;
  entityName: string | null;
  entityId: number | null;
  description: string | null;
  isSuccess: boolean | null;
  createdAt: string;
};

/** Raw response of `GET /activity-log/get-by-entity`. */
export type TActivityLogResponse = TAccountingGenericResult<TActivityLog>;

/**
 * Query of `GET /activity-log/get-approval` ("Lịch sử xác nhận"). Dates are `YYYY-MM-DD`; paging is DataTables style
 * (`start` = index of the first row).
 */
export type TActivityLogApprovalParams = {
  serviceName: string;
  entityName?: string;
  /** `systemId` of the staff who confirmed / unlocked. */
  userId?: number;
  fromDate?: string;
  toDate?: string;
  keyword?: string;
  start: number;
  draw?: number;
  pageSize: number;
};

/** Item of `GET /activity-log/get-approval`: an activity log covering one or more records (`entityIds`). */
export type TActivityLogApproval = TActivityLog & {
  entityIds: number[] | null;
};

/** Raw response of `GET /activity-log/get-approval` (DataTables shaped `results[0]`). */
export type TActivityLogApprovalResponse =
  TAccountingListResponse<TActivityLogApproval>;

/* -------------------------------------------------------------------------- */
/*                   Provider reports (airline statements)                    */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*                                 Hotel bookings                             */
/* -------------------------------------------------------------------------- */

/**
 * Hotel shift closing record of `GET /cuscare/ListHotelShiftClosing` / `GET /cuscare/GetHotelShiftClosing`
 * (customer care "Khách sạn" tab). Dates have no offset; amounts are VND.
 */
export type THotelShiftClosing = {
  id: number;
  partnerCode: string | null;
  bookingCode: string | null;
  /** Same codes as the old app: `ISSUE_SUCCESS` (xuất), `REFUND` (hoàn), `VOID` (hủy). */
  status: string | null;
  /** Note of the record ("Ghi chú"). */
  remark: string | null;
  /** Provider system code ("Nhà cung cấp"). */
  companyCode: string | null;
  hotelName: string | null;
  /** Guest name. */
  passengerName: string | null;
  /** Net price ("Giá NET"). */
  totalAmount: number | null;
  /** Selling price ("Giá thu"). */
  collection: number | null;
  /** Conversion fee ("Phí chuyển đổi"). */
  totalConversion: number | null;
  createdDate: string | null;
  checkIn: string | null;
  checkOut: string | null;
  roomCount: number | null;
  isLock: SHIFT_CLOSING_LOCK_STATUS | null;
  userId?: number | null;
  operator?: string | null;
  via?: string | null;
  marketingFee?: number | null;
};

/** Query of `GET /cuscare/ListHotelShiftClosing`. Dates are `YYYY-MM-DD`. */
export type THotelShiftClosingListParams = Partial<TAccountingPagingParams> & {
  keyword?: string;
  from?: string;
  to?: string;
  bookingCode?: string;
  status?: string;
  /** Provider system code ("Nhà cung cấp"). */
  companyCode?: string;
  partnerCode?: string;
  userId?: number;
  /** Lock state of the listed records ("Dữ liệu treo" = `PENDING`, "Xử lý dữ liệu" = `UNCONFIRMED`). */
  isLock?: SHIFT_CLOSING_LOCK_STATUS;
  /** Lets the service return large pages for the Excel export. */
  isExport?: boolean;
};

/** Raw list response of `GET /cuscare/ListHotelShiftClosing` (DataTables shaped `results[0]`). */
export type THotelShiftClosingListResponse =
  TAccountingListResponse<THotelShiftClosing>;

/** Raw response of `GET /cuscare/GetHotelShiftClosing` (the record is `results[0].data[0]`). */
export type THotelShiftClosingDetailResponse =
  TAccountingListResponse<THotelShiftClosing>;

/* -------------------------------------------------------------------------- */
/*                                Reconciliation                              */
/* -------------------------------------------------------------------------- */

/** Select option of a company bank account (see `useBankAccountOptions`). */
export type TBankAccountOption = {
  /** Bank account id (`BankAccount.Id`, used as `bankAccountId` / `bankId`). */
  value: number;
  label: string;
  bankCode: string | null;
};

/**
 * Multipart body of `POST /accounting/ImportExcelProviderReport` ("Import dữ liệu bán"): keys are sent as-is, they
 * are the form field names bound by the accounting service.
 */
export type TImportProviderReportPayload = {
  file: File;
  /** Airline / provider code, e.g. `VJ`, `VN-ANV`. */
  providerCode: string;
  /** Issue date of the imported rows, `DD-MM-YYYY` (parsed strictly by the service). */
  issueDate: string;
};

/** Body of `POST /accounting/comparedata` (airline data comparison of one airline on one issue day). */
export type TCompareProviderDataPayload = {
  providerCode: string;
  /** Issue day, `DD-MM-YYYY`. */
  issueTime: string;
};

/** Customer care ticket (`tblTicket`) returned by the airline data comparison. */
export type TReconciliationTicket = {
  id: number;
  bookingCode: string | null;
  passengerName: string | null;
  ticketNumber: string | null;
  issueDate: string | null;
  airlineCode: string | null;
  status: string | null;
  /** Amount shown as "Giá Net" of the system side (the old comparator's column). */
  totalAmount: number | null;
  fareAmount: number | null;
  feeAmount: number | null;
  taxAmount: number | null;
  /** `null` / `0` = not confirmed yet ("Chưa chốt ca"). */
  isLock: number | null;
};

/** Row imported from an airline file (`ProviderReport`), returned by the airline data comparison. */
export type TProviderReportRow = {
  id: number;
  providerCode: string | null;
  passengerName: string | null;
  status: string | null;
  ticketNumber: string | null;
  bookingCode: string | null;
  netAmount: number | null;
  fareAmount: number | null;
  taxAmount: number | null;
  feeAmount: number | null;
  issueTime: string | null;
};

/** Ticket whose amount differs between the system and the airline file. */
export type TProviderDataMismatch = {
  ticketNumber: string | null;
  /** System ticket total minus the summed airline NET amount. */
  netAmount: number | null;
  /** e.g. "Multiple tickets found (2) for this ticket number". */
  note: string | null;
};

/** Differences of the airline data comparison (`ReconciliationDifferences`). */
export type TProviderDataCompareDifferences = {
  /** System tickets missing from the airline file. */
  inTable1NotInTable2: TReconciliationTicket[] | null;
  /** Airline file rows missing from the system tickets. */
  inTable2NotInTable1: TProviderReportRow[] | null;
  dataMismatches: TProviderDataMismatch[] | null;
  /** System tickets not confirmed yet (`isLock` null / 0), matched or not. */
  unconfirmedTickets: TReconciliationTicket[] | null;
};

/** Result of the airline data comparison (`results[0].data[0]` of `comparedata`). */
export type TProviderDataCompareResult = {
  totalRecordsTable1: number;
  totalRecordsTable2: number;
  matchedCount: number;
  unmatchedCount: number;
  totalUnconfirmedRecords: number;
  differences: TProviderDataCompareDifferences | null;
};

/** Raw response of `POST /accounting/comparedata`. */
export type TProviderDataCompareResponse =
  TAccountingListResponse<TProviderDataCompareResult>;

/**
 * Cached airline data comparison: the result with the request that produced it (a `fixedCacheKey` mutation does not
 * expose its `originalArgs`, and the comparison is re-run after a batch action).
 */
export type TProviderDataCompareEntry = {
  params: TCompareProviderDataPayload;
  result: TProviderDataCompareResult;
};

/** Multipart body of `ImportBankTransaction` ("Import dữ liệu giao dịch"), keys sent as-is. */
export type TImportBankTransactionPayload = {
  file: File;
  /** Bank code of the account, selects the statement layout (`TCB`, `MB`, `VCB`). */
  bankCode: string;
  /** Company bank account id. */
  bankId: number;
};

/** Body of `POST /accounting/CompareBankData` (bank statement comparison of one account). */
export type TCompareBankDataPayload = {
  bankId: number;
  /** First day, `DD-MM-YYYY`. */
  from: string;
  /** Day after the last one, `DD-MM-YYYY` (the service range is `[from, to)`). */
  to: string;
};

/** Bank statement row (`BankTransaction`) returned by the bank statement comparison. */
export type TBankStatementRow = {
  id: number;
  bankId: number;
  transactionDate: string;
  referenceNumber: string | null;
  remittersBank: string | null;
  accountNumber: string | null;
  accountName: string | null;
  description: string | null;
  amount: number | null;
  runningBalance: number | null;
};

/** Transaction whose amount differs between the system and the bank statement. */
export type TBankDataMismatch = {
  /** Funds transfer / reference number shared by both sides. */
  fundsTransfer: string | null;
  /** System amount minus bank statement amount. */
  amount: number | null;
};

/** Differences of the bank statement comparison (`BankReconciliationDifferences`). */
export type TBankDataCompareDifferences = {
  /** System deposit transactions missing from the bank statement. */
  inTable1NotInTable2: TDepositTransaction[] | null;
  /** Bank statement rows missing from the system deposit transactions. */
  inTable2NotInTable1: TBankStatementRow[] | null;
  dataMismatches: TBankDataMismatch[] | null;
};

/** Result of the bank statement comparison (`results[0].data[0]` of `CompareBankData`). */
export type TBankDataCompareResult = {
  totalRecordsTable1: number;
  totalRecordsTable2: number;
  matchedCount: number;
  unmatchedCount: number;
  differences: TBankDataCompareDifferences | null;
};

/** Raw response of `POST /accounting/CompareBankData`. */
export type TBankDataCompareResponse =
  TAccountingListResponse<TBankDataCompareResult>;

/** Cached bank statement comparison: the result with the request that produced it (see `TProviderDataCompareEntry`). */
export type TBankDataCompareEntry = {
  params: TCompareBankDataPayload;
  result: TBankDataCompareResult;
};

/** Body of `POST /accounting/CreateMultiDepositTransaction` (deposit transactions from bank statement rows). */
export type TCreateMultiDepositTransactionPayload = {
  /** Bank statement row ids (`BankTransaction.Id`). */
  ids: number[];
  partnerCode: string;
  type: DEPOSIT_TRANSACTION_TYPE;
};

/** Payload of `useAccountingKeywordFilter`. */
export type TUseAccountingKeywordFilterPayload<
  T extends Record<string, unknown>,
> = {
  /** Current URL filters (without pagination keys). */
  filterParams: T;
  /** `useQueryHandle().handleChangeFilter`. */
  handleChangeFilter: (values: T) => void;
  /** URL key of the keyword (defaults to `keyword`). */
  keywordKey?: keyof T & string;
};

/** Payload of `useAccountingDefaultQueryParams`. */
export type TUseAccountingDefaultQueryParamsPayload<
  T extends Record<string, unknown>,
> = {
  /** Current URL query params (`useQueryHandle().queryParams`, pagination included). */
  queryParams: T;
  /** Builds the default filters, evaluated when the page is opened (e.g. a period relative to today). */
  buildDefaultParams: () => Partial<T>;
};
